import type { CeoCadenceItem } from './ceoCockpitTypes'
import { formatCurrencyCompact } from '../cfoFormat'

/**
 * A chip is a number and its name.
 *
 * "64% · Working well" tells a reader nothing they can act on — 64% of what?
 * The label has to name the measure, not deliver the verdict; the verdict is
 * already carried by the colour and by the card the chip opens.
 */
const CHIP_LABEL: Record<string, string | ((item: CeoCadenceItem, shown: string) => string)> = {
  sales: (item, shown) => {
    // The sales family carries two different claims — the company against its
    // plan, and one person against their own pace. A fixed label would
    // contradict the card it opens.
    const headline = item.headline.toLowerCase()
    if (headline.includes('behind pace')) return 'of one rep’s sales pace'
    if (headline.includes('ahead')) return 'sales ahead of plan'
    return 'sales behind plan'
  },
  collections: 'receivables past due',
  supply: 'products short of stock',
  // One rule, two shapes of claim: "2 locations are over their labor target"
  // counts sites, "Portland labor is 42.1% of sales" states a rate.
  labor: (_item, shown) => (shown.includes('%') ? 'labor cost of sales' : 'sites over labor target'),
  cash: 'cash against the floor',
  retention: 'revenue retained',
  unit_economics: 'months to earn a customer back',
  margin: 'margin on a product',
  margin_quality: 'gross margin in one odd month',
  // Keyed by the detector, not the family. A concentration finding lives in the
  // sales family, so falling through to the family label printed "91% sales
  // behind plan" over a claim about the top five customers — a chip that
  // contradicts the card it opens.
  monthly_margin_outlier: 'gross margin in one odd month',
  monthly_margin: 'gross margin over three months',
  monthly_concentration: 'of sales in the top five customers',
  monthly_retention: 'net revenue retention',
  monthly_working_capital: 'of cash in slow stock',
  weekly_sales_trend: 'against the weekly norm',
  weekly_labor: 'labor cost of sales',
  weekly_aging: 'receivables over 60 days',
}

/**
 * The number the chip shows.
 *
 * Taken from the headline first, because the chip is a shortcut to that
 * sentence and a reader who taps it must land on the number they just read.
 * Only when the headline carries no number does it fall back — and then to the
 * item's own evidence rather than to anything about a different subject.
 */
export function chipNumberFor(item: CeoCadenceItem): string {
  // Order matters: a decimal has to be tried before the integer pattern, or
  // "18.8 months" arrives on the chip as a flat "18".
  const match = item.headline.match(
    /(\$[\d,]+(?:\.\d+)?\s?[KMB]?|\d+(?:\.\d+)?\s?(?:points?|%)|\b\d+\.\d+\b|\b\d[\d,]*\b)/,
  )
  if (match) {
    const raw = match[1].replace(/\s+/g, ' ').trim()
    // Headlines are not uniform about compaction ($156K in one rule, $32,403 in
    // another). A chip is a glance, so it always compacts.
    if (raw.startsWith('$') && !/[KMB]$/.test(raw)) {
      const value = Number(raw.replace(/[$,]/g, ''))
      if (Number.isFinite(value) && Math.abs(value) >= 10000) return formatCurrencyCompact(value)
    }
    return raw
  }
  if (typeof item.exposure_value === 'number' && Math.abs(item.exposure_value) >= 500) {
    return formatCurrencyCompact(item.exposure_value)
  }
  return '✓'
}

export function chipLabelFor(item: CeoCadenceItem): string {
  const entry = CHIP_LABEL[item.key] ?? CHIP_LABEL[item.family]
  if (typeof entry === 'function') return entry(item, chipNumberFor(item))
  if (entry) return entry
  // Deliberately not the family name: "data quality" or "unit economics" is a
  // category, not a measure, and a chip labelled with one answers none of the
  // reader's question. Fall back to the words around the number instead.
  return measureFromHeadline(item.headline) || item.family.replace(/_/g, ' ')
}

/**
 * What a cadence's section is called, given what it is carrying. A week whose
 * only item is a genuine strength is not "nothing drifting", and a quiet week
 * is not "worth watching".
 */
export function cadenceCaption(cadence: 'today' | 'week' | 'month', items: CeoCadenceItem[]): string {
  const noun = cadence === 'today' ? 'Today' : cadence === 'week' ? 'This week' : 'This month'
  if (items.some((item) => item.kind === 'decide')) return `${noun} · needs a decision`
  if (items.some((item) => item.kind === 'watch')) return `${noun} · worth watching`
  if (items.length > 0) return `${noun} · what is working`
  return noun
}


/**
 * The words immediately around the number, when no label is configured.
 *
 * Not a substitute for a written label — it is the last resort that keeps a
 * chip from announcing a category instead of a measure.
 */
function measureFromHeadline(headline: string): string | null {
  const match = headline.match(
    /(?:\$[\d,]+(?:\.\d+)?\s?[KMB]?|\d+(?:\.\d+)?\s?%?)\s+(?:of\s+)?([a-z][a-z\s]{2,28})/i,
  )
  if (!match) return null
  return match[1].trim().replace(/\s+(is|are|was|were|does|do)$/i, '').toLowerCase() || null
}
